import { ClinicianInactiveIcon } from '@/components/clinicians/ClinicianInactiveIcon';
import { ClinicianLicenseChip } from '@/components/clinicians/ClinicianLicenseChip';
import { useDisciplineColorMap } from '@/components/disciplines/useDisciplineColorMap';
import {
    HeadingCaptionLabel,
    LeftRailNoneLabel,
    LeftRailTextButton,
    LeftRailValueTypography,
} from '@/components/patients/PatientLeftRail/PatientLeftRail';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { TEXT_GRAY } from '@/constants/colors';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { employmentTypeSuffix } from '@/types/clinician';
import { useConfigValue } from '@/utils/useConfigValue';
import { Box, Skeleton } from '@mui/material';
import { grey } from '@mui/material/colors';
import { FC, Fragment } from 'react';

import { CareTeamClinicianFragmentFragment } from './PatientCareTeamInfo.fragments.gql.types';
import { sortRnsFirst, withCaseManagerRoles } from './PatientCareTeamInfo.utils';

// Stable empty map, so a chip rendered before the discipline colors load keeps its identity.
const EMPTY_DISCIPLINE_COLORS = new Map<string, string>();

export type ICareTeamClinician = CareTeamClinicianFragmentFragment;

type PatientCareTeamInfoPatient = {
    id: string;
    careTeams: { clinicians: ICareTeamClinician[] }[];
    permissions: { editCareTeam: boolean };
};

const CareTeamSkeletonRow = () => (
    <HorizontalStack spacing={2} alignItems='center'>
        <LeftRailValueTypography>
            <Skeleton variant='text' width={160} />
        </LeftRailValueTypography>
        <Skeleton variant='rounded' width={36} height={16} />
    </HorizontalStack>
);

export const CareTeamInfoSkeleton = () => (
    <VerticalStack spacing={1}>
        <HorizontalStack justifyContent='space-between'>
            <HeadingCaptionLabel>Care Team</HeadingCaptionLabel>
            <LeftRailTextButton sx={{ visibility: 'hidden' }} aria-hidden tabIndex={-1}>
                Edit
            </LeftRailTextButton>
        </HorizontalStack>
        <VerticalStack spacing={0.5}>
            <CareTeamSkeletonRow />
            <CareTeamSkeletonRow />
            <CareTeamSkeletonRow />
        </VerticalStack>
    </VerticalStack>
);

type CareTeamClinicianWithRole = ICareTeamClinician & {
    role?: string;
};

export const PatientCareTeamInfo = ({
    patient,
    showDisciplineCodes = false,
    hasRecommendationFlow,
    hasEffectiveDates,
    showAttributeChips,
}: {
    patient: PatientCareTeamInfoPatient;
    showDisciplineCodes?: boolean;
    hasRecommendationFlow: boolean;
    hasEffectiveDates: boolean;
    showAttributeChips: boolean;
}) => {
    const { openDialog } = useDialog();
    const applyRnCaseManagerLabel = useConfigValue('care_team_case_manager_label_for_rn');
    const disciplineColors = useDisciplineColorMap();

    let careTeamClinicians: CareTeamClinicianWithRole[] = patient.careTeams.flatMap(
        (careTeam) => careTeam.clinicians,
    );
    if (applyRnCaseManagerLabel) {
        // Label RNs as Case Managers and float them to the top of the care team list.
        careTeamClinicians = sortRnsFirst(withCaseManagerRoles(careTeamClinicians));
    }

    const editCareTeamDialogType = hasEffectiveDates
        ? DialogType.EditPatientCareTeamDatesRoles
        : hasRecommendationFlow
        ? DialogType.EditPatientCareTeamRecommendation
        : DialogType.EditPatientCareTeam;

    return (
        <VerticalStack spacing={1}>
            <HorizontalStack justifyContent='space-between'>
                <HeadingCaptionLabel>Care Team</HeadingCaptionLabel>
                {patient.permissions.editCareTeam && (
                    <LeftRailTextButton
                        onClick={() =>
                            openDialog(editCareTeamDialogType, { patientId: patient.id })
                        }
                    >
                        Edit
                    </LeftRailTextButton>
                )}
            </HorizontalStack>
            <VerticalStack spacing={0.5}>
                {careTeamClinicians.length > 0 ? (
                    careTeamClinicians.map((clinician) => (
                        <CareTeamClinician
                            key={clinician.id}
                            clinician={clinician}
                            disciplineColors={
                                showDisciplineCodes || showAttributeChips ? disciplineColors : null
                            }
                            showAttributeChips={showAttributeChips}
                        />
                    ))
                ) : (
                    <LeftRailNoneLabel />
                )}
            </VerticalStack>
        </VerticalStack>
    );
};

interface ICareTeamClinicianProps {
    clinician: CareTeamClinicianWithRole;
    disciplineColors: Map<string, string> | null;
    showAttributeChips: boolean;
    displayEmail?: boolean;
}

export const CareTeamClinician: FC<ICareTeamClinicianProps> = ({
    clinician,
    disciplineColors,
    showAttributeChips,
}) => {
    const licenses = clinician.licenses?.length ? ` (${clinician.licenses.join(', ')})` : '';
    const employmentType = employmentTypeSuffix(clinician.employmentType);

    if (showAttributeChips) {
        return (
            <VerticalStack spacing={0}>
                {clinician.role && <HeadingCaptionLabel>{clinician.role}</HeadingCaptionLabel>}

                <HorizontalStack spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap>
                    <ClinicianLicenseChip
                        disciplineCodes={clinician.disciplines.map((discipline) => discipline.code)}
                        licenses={clinician.licenses ?? []}
                        disciplineColors={disciplineColors ?? EMPTY_DISCIPLINE_COLORS}
                    />
                    <LeftRailValueTypography
                        sx={{ color: TEXT_GRAY, textDecoration: 'none', flex: 1 }}
                    >
                        {clinician.fullName}
                        {employmentType}
                    </LeftRailValueTypography>
                    {!clinician.isActive && <ClinicianInactiveIcon />}
                </HorizontalStack>
            </VerticalStack>
        );
    }

    return (
        <VerticalStack spacing={0}>
            {clinician.role && <HeadingCaptionLabel>{clinician.role}</HeadingCaptionLabel>}

            <LeftRailValueTypography
                sx={{
                    color: TEXT_GRAY,
                    textDecoration: 'none',
                }}
            >
                {disciplineColors ? (
                    <>
                        {clinician.fullName}
                        {clinician.disciplines.length > 0 && (
                            <>
                                {' ('}
                                {clinician.disciplines.map((discipline, index) => (
                                    <Fragment key={discipline.id}>
                                        {index > 0 && ', '}
                                        <Box
                                            component='span'
                                            sx={{
                                                fontWeight: 600,
                                                color:
                                                    disciplineColors.get(discipline.code) ??
                                                    grey[600],
                                            }}
                                        >
                                            {discipline.code}
                                        </Box>
                                    </Fragment>
                                ))}
                                {')'}
                            </>
                        )}
                    </>
                ) : (
                    clinician.fullName + licenses
                )}
            </LeftRailValueTypography>
        </VerticalStack>
    );
};
